import { useApi } from 'api/api-context';
import {
  DataMultiSelect,
  DataMultiSelectInheritedProps,
  DataSelect,
  DataSelectInheritedProps,
} from 'components/selects/DataSelect';
import { nanoid } from 'nanoid';
import { useCallback, useMemo } from 'react';

const EMPTY_REMOTE_SOURCE_IDS: number[] = [];

/**
 * The shared props of the select components.
 */
interface RemoteSourceSelectProp {
  showDiscarded?: boolean;
  excludeRemoteSourceIds?: number[];
}

/**
 * Fetches remote sources from API.
 */
function useRemoteSourceSelect({ showDiscarded = false, excludeRemoteSourceIds = [] }: RemoteSourceSelectProp) {
  const { getAction } = useApi();
  const excludeRemoteSourceIdsKey = excludeRemoteSourceIds.join(',');
  const excludedRemoteSourceIds = useMemo(() => new Set(excludeRemoteSourceIds), [excludeRemoteSourceIdsKey]);

  const fetchRemoteSources = useCallback(async () => {
    const action = getAction('RemoteSourceList');
    const remoteSources = await action();

    return remoteSources
      .filter((source) => (showDiscarded ? true : source.status))
      .filter((source) => !excludedRemoteSourceIds.has(source.remoteSourceId))
      .map((source) => ({
        label: (source.status ? '' : '[NEAKTÍVNY] ') + source.name,
        value: source.remoteSourceId,
      }));
  }, [getAction, showDiscarded, excludedRemoteSourceIds]);

  return { fetchRemoteSources };
}

/**
 * The type of the RemoteSource Select component.
 */
type RemoteSourceSelectProps = RemoteSourceSelectProp & DataSelectInheritedProps;

/**
 * The type of the RemoteSource Multiselect component.
 */
type RemoteSourceMultiSelectProps = RemoteSourceSelectProp & DataMultiSelectInheritedProps;

/**
 * Select input for remote sources.
 */
export function RemoteSourceSelect({
  showDiscarded = true,
  excludeRemoteSourceIds = EMPTY_REMOTE_SOURCE_IDS,
  ...props
}: RemoteSourceSelectProps) {
  const excludeRemoteSourceIdsKey = excludeRemoteSourceIds.join(',');
  const key = useMemo(() => nanoid(), [showDiscarded, excludeRemoteSourceIdsKey]);
  const { fetchRemoteSources } = useRemoteSourceSelect({ showDiscarded, excludeRemoteSourceIds });
  return <DataSelect key={key} action={fetchRemoteSources} labelProp="label" valueProp="value" {...props} />;
}

/**
 * Select input for remote sources (multiple).
 */
export function RemoteSourceMultiSelect({
  showDiscarded = true,
  excludeRemoteSourceIds = EMPTY_REMOTE_SOURCE_IDS,
  ...props
}: RemoteSourceMultiSelectProps) {
  const excludeRemoteSourceIdsKey = excludeRemoteSourceIds.join(',');
  const key = useMemo(() => nanoid(), [showDiscarded, excludeRemoteSourceIdsKey]);

  const { fetchRemoteSources } = useRemoteSourceSelect({ showDiscarded, excludeRemoteSourceIds });
  return <DataMultiSelect key={key} action={fetchRemoteSources} labelProp="label" valueProp="value" {...props} />;
}
